import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Eye, Users, ListChecks, ChevronRight, Lock, Plus, ShieldAlert } from "lucide-react";
import { api, formatPrice } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/ui/button";

const StatCard = ({ icon: Icon, value, label, testid }) => (
  <div className="rounded-3xl border border-border bg-white p-6 shadow-sm" data-testid={testid}>
    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brandblue-light text-brandblue"><Icon size={20} /></div>
    <div className="mt-4 font-display text-3xl font-extrabold text-navy">{value}</div>
    <div className="text-sm text-slate-500">{label}</div>
  </div>
);

const MakelaarDashboard = () => {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [pendingReason, setPendingReason] = useState("");

  useEffect(() => {
    api("/makelaar/dashboard").then(setData).catch((e) => {
      if (e.status === 403) {
        setPendingReason(e.message || "Je makelaar-account wacht nog op goedkeuring.");
        setForbidden(true);
      } else if (e.status === 402) {
        setForbidden(true);
      } else setData(false);
    });
  }, []);

  if (forbidden) {
    return (
      <div>
        <PageHeader icon={Building2} title={t("p2.mk.title")} />
        <div className="mx-auto max-w-md px-4 py-20 text-center">
          <ShieldAlert className="mx-auto text-amber-500" size={44} />
          <p className="mt-4 text-slate-600">{pendingReason || t("p2.mk.forbidden")}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild variant="outline" className="rounded-xl">
              <Link to="/makelaar/wacht-op-goedkeuring">Status bekijken</Link>
            </Button>
            <Button asChild className="rounded-xl bg-brandblue hover:bg-brandblue/90">
              <Link to="/prijzen">{t("p2.mk.viewPlans")}</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader icon={Building2} title={t("p2.mk.title")} subtitle={t("p2.mk.subtitle")} />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Button asChild className="rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="new-huur-btn">
            <Link to="/makelaar/woning/nieuw"><Plus size={16} className="mr-2" /> Nieuwe huurwoning</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-xl" data-testid="new-koop-btn">
            <Link to="/koop/makelaar/wizard"><Plus size={16} className="mr-2" /> Nieuwe koopwoning</Link>
          </Button>
          <Button asChild variant="outline" className="ml-auto rounded-xl">
            <Link to="/koop/makelaar">KOOP dashboard →</Link>
          </Button>
        </div>
        {data === null ? (
          <div className="grid gap-5 sm:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-36 animate-pulse rounded-3xl bg-slate-100" />)}</div>
        ) : !data ? (
          <p className="text-center text-slate-500">{t("common.loadFail")}</p>
        ) : (
          <>
            <div className="grid gap-5 sm:grid-cols-3">
              <StatCard icon={ListChecks} value={data.active_listings} label={t("p2.mk.active")} testid="mk-active" />
              <StatCard icon={Eye} value={data.total_views} label={t("p2.mk.views")} testid="mk-views" />
              <StatCard icon={Users} value={data.total_leads} label={t("p2.mk.leads")} testid="mk-leads" />
            </div>

            <h2 className="mt-10 font-display text-2xl font-extrabold text-navy">{t("p2.mk.top")}</h2>
            {data.top_listings?.length ? (
              <div className="mt-5 space-y-3">
                {data.top_listings.map((l) => (
                  <Link
                    key={l.id}
                    to={`/makelaar/woning/${l.id}/kandidaten`}
                    data-testid={`mk-listing-${l.id}`}
                    className="flex items-center gap-4 rounded-2xl border border-border bg-white p-4 shadow-sm transition-colors hover:border-brandblue"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-display font-bold text-navy">{l.title}</div>
                      <div className="text-sm text-slate-500">{l.street}, {l.city} · {formatPrice(l.price)}</div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1"><Eye size={14} /> {l.view_count}</span>
                      <span className="flex items-center gap-1"><Users size={14} /> {l.lead_count}</span>
                      <ChevronRight size={18} className="text-brandblue" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="mt-5 rounded-3xl border border-dashed border-border p-12 text-center text-slate-500">
                {t("p2.mk.noListings")}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MakelaarDashboard;
